import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { PAGE_PERMISSIONS, type Permission } from '@pirochi/shared';
import { useAuth } from '@/app/providers/AuthProvider';
import { AppShell } from '@/app/layouts/AppShell';
import { Loading } from '@/components/ui';
import { t } from '@/i18n/ar';
import { isChunkLoadError, reloadForNewVersion } from '@/utils/app-version';
import { RouteError } from './RouteError';

/** Lazy page; after a new deployment the old chunk is gone, so reload once to get the new version. */
const page = (f: () => Promise<{ default: React.ComponentType }>) =>
  lazy(() =>
    f().catch((e: unknown) => {
      if (isChunkLoadError(e) && reloadForNewVersion()) return new Promise<never>(() => {});
      throw e;
    }),
  );
const Dashboard = page(() => import('@/features/dashboard/DashboardPage'));
const Ai = page(() => import('@/features/ai/AiAssistantPage'));
const DayBoard = page(() => import('@/features/events/DayBoardPage'));
const Calendar = page(() => import('@/features/events/CalendarPage'));
const Pipeline = page(() => import('@/features/pipeline/PipelinePage'));
const Customers = page(() => import('@/features/customers/CustomersPage'));
const Orders = page(() => import('@/features/orders/OrdersPage'));
const Logistics = page(() => import('@/features/logistics/LogisticsPage'));
const Products = page(() => import('@/features/products/ProductsPage'));
const Pricing = page(() => import('@/features/pricing/PricingPage'));
const Pos = page(() => import('@/features/pos/PosPage'));
const Commission = page(() => import('@/features/commission/CommissionPage'));
const Finance = page(() => import('@/features/finance/FinancePage'));
const Accounting = page(() => import('@/features/accounting/AccountingPage'));
const Suppliers = page(() => import('@/features/suppliers/SuppliersPage'));
const Inventory = page(() => import('@/features/inventory/InventoryPage'));
const Team = page(() => import('@/features/team/TeamPage'));
const Attendance = page(() => import('@/features/attendance/AttendancePage'));
const Reports = page(() => import('@/features/reports/ReportsPage'));
const Statistics = page(() => import('@/features/statistics/StatisticsPage'));
const Appointments = page(() => import('@/features/appointments/AppointmentsPage'));
const Seasonal = page(() => import('@/features/appointments/SeasonalPage'));
const Quality = page(() => import('@/features/appointments/QualityPage'));
const Settings = page(() => import('@/features/settings/SettingsPage'));
const Login = page(() => import('@/features/auth/LoginPage'));
const ClientPortal = page(() => import('@/features/client/ClientPortalPage'));

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

function Guard({ id, perm, children }: { id?: string; perm?: Permission[]; children: ReactNode }) {
  const { can } = useAuth();
  const needed = perm ?? (id ? [PAGE_PERMISSIONS[id] as Permission] : []);
  const ok = perm ? perm.some((p) => can(p)) : needed.every((p) => can(p));
  if (!ok) return <div className="card p-8 text-center text-[#a84d62]">{t.forbidden}</div>;
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

const g = (id: string, el: ReactNode) => <Guard id={id}>{el}</Guard>;

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<Loading />}>
        <Login />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/client/:token',
    element: (
      <Suspense fallback={<Loading />}>
        <ClientPortal />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      {
        // page errors are shown inside the shell (sidebar stays usable)
        element: <Outlet />,
        errorElement: <RouteError />,
        children: [
          { index: true, element: g('dashboard', <Dashboard />) },
          { path: 'ai', element: g('ai', <Ai />) },
          { path: 'day-board', element: g('dayBoard', <DayBoard />) },
          { path: 'calendar', element: g('calendar', <Calendar />) },
          { path: 'pipeline', element: g('pipeline', <Pipeline />) },
          { path: 'customers', element: g('customers', <Customers />) },
          { path: 'orders', element: g('orders', <Orders />) },
          { path: 'logistics', element: g('logistics', <Logistics />) },
          { path: 'products', element: g('products', <Products />) },
          { path: 'pricing', element: g('pricing', <Pricing />) },
          { path: 'pos', element: g('pos', <Pos />) },
          { path: 'commission', element: g('commission', <Commission />) },
          { path: 'finance', element: g('finance', <Finance />) },
          { path: 'accounting', element: g('accounting', <Accounting />) },
          { path: 'suppliers', element: g('suppliers', <Suppliers />) },
          { path: 'inventory', element: g('inventory', <Inventory />) },
          { path: 'team', element: g('team', <Team />) },
          { path: 'attendance', element: g('attendance', <Attendance />) },
          { path: 'reports', element: g('reports', <Reports />) },
          { path: 'statistics', element: g('statistics', <Statistics />) },
          { path: 'appointments', element: g('appointments', <Appointments />) },
          { path: 'seasonal', element: g('seasonal', <Seasonal />) },
          { path: 'quality', element: g('quality', <Quality />) },
          {
            path: 'settings',
            element: (
              <Guard
                perm={['settings.manage', 'users.manage', 'accounting.manage', 'migration.run', 'audit.view']}
              >
                <Settings />
              </Guard>
            ),
          },
          { path: '*', element: <div className="card p-8 text-center text-[#a84d62]">{t.notFound}</div> },
        ],
      },
    ],
  },
]);
